import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Compass,
  Lightbulb,
  BookOpen,
  Target,
  Globe2,
} from "lucide-react";
import { PageHero, SectionHeader, EmeraldButton } from "@/components/Bits";
import { LIFESTYLE } from "@/lib/brand";

const visionPanels = [
  {
    icon: Compass,
    label: "Vision",
    title: "Empowering Professionals Nationwide",
    body: "Supporting professionals as they build more intentional and fulfilling futures.",
  },
  {
    icon: Lightbulb,
    label: "Focus Area",
    title: "Digital Education & Productivity",
    body: "Providing practical resources that help professionals reclaim their time and attention.",
  },
  {
    icon: BookOpen,
    label: "Commitment",
    title: "Evidence-Based Personal Development",
    body: "Combining education, strategy, and real-world experience to support meaningful growth.",
  },
  {
    icon: Target,
    label: "Impact Goal",
    title: "Economic Empowerment Through Education",
    body: "Helping individuals create more options, opportunities, and confidence for their future.",
  },
];

const initiatives = [
  {
    title: "Digital Literacy & Productivity Education",
    body: "Educational programming designed to help emerging professionals build the digital tools, focus practices, and time strategies required for modern work.",
  },
  {
    title: "Women's Economic Empowerment",
    body: "Initiatives that expand financial literacy, entrepreneurial skill, and income opportunity for professionals navigating mid-career transitions.",
  },
  {
    title: "Workforce Readiness for Emerging Professionals",
    body: "Education and mentorship pathways for professionals seeking greater economic mobility and long-term self-sufficiency.",
  },
  {
    title: "Community Resource Distribution",
    body: "A growing library of digital tools and frameworks shared with community partners, foundations, and educational institutions.",
  },
];

const Impact = () => {
  return (
    <div data-testid="page-impact">
      <PageHero
        overline="Impact & Grant Readiness"
        title="Creating Pathways to Economic Empowerment"
        subtitle="The Exit Strategy Society is committed to helping professionals reclaim their time, strengthen their confidence, expand their opportunities, and build sustainable pathways toward greater freedom and fulfillment."
        image={LIFESTYLE.womenLeading}
      />

      <section className="ess-section bg-ess-paper">
        <div className="ess-container">
          <div className="max-w-3xl">
            <p className="ess-overline">Our Philosophy</p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl text-ess-emerald leading-tight">
              Education changes lives — for individuals and communities.
            </h2>
            <span className="ess-divider mt-6" />
            <p className="mt-6 text-ess-slate leading-relaxed">
              We believe education, personal development, digital literacy, and
              entrepreneurship can create life-changing opportunities for
              individuals and communities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ess-emerald/15 mt-14">
            {visionPanels.map(({ icon: Icon, label, title, body }) => (
              <div
                key={label}
                data-testid={`impact-panel-${label.replace(/\s+/g, "-").toLowerCase()}`}
                className="bg-white p-8 md:p-10 group hover:bg-ess-paper transition-colors"
              >
                <div className="w-12 h-12 border border-ess-emerald/20 flex items-center justify-center text-ess-emerald group-hover:bg-ess-emerald group-hover:text-ess-gold group-hover:border-ess-emerald transition-all">
                  <Icon size={20} strokeWidth={1.4} />
                </div>
                <p className="ess-overline mt-6">{label}</p>
                <h3 className="font-display text-2xl md:text-[26px] text-ess-emerald mt-3 leading-tight">
                  {title}
                </h3>
                <p className="text-ess-slate text-sm mt-3 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ess-section bg-white">
        <div className="ess-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader
              overline="Grant Readiness"
              title="Supporting Education, Workforce Development, and Economic Opportunity."
              subtitle="The Exit Strategy Society seeks to expand access to education, professional development, digital literacy, entrepreneurship education, and financial wellness resources."
            />
            <p className="mt-6 text-ess-slate leading-relaxed">
              We welcome opportunities to collaborate with foundations, corporations,
              community organizations, educational institutions, and workforce
              development initiatives that share our commitment to economic
              empowerment and lifelong learning.
            </p>
            <EmeraldButton as="link" to="/partnerships" className="mt-8" data-testid="impact-partner-cta">
              Explore Partnership Opportunities <ArrowRight size={14} className="ml-3" />
            </EmeraldButton>
          </div>
          <div className="lg:col-span-7">
            <div className="space-y-px bg-ess-blush border border-ess-blush">
              {initiatives.map((i) => (
                <div key={i.title} className="bg-white p-8">
                  <h3 className="font-display text-2xl text-ess-emerald">{i.title}</h3>
                  <p className="text-ess-slate mt-3 leading-relaxed">{i.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LOOKING AHEAD */}
      <section className="ess-section bg-ess-blush" data-testid="impact-looking-ahead">
        <div className="ess-container max-w-4xl mx-auto text-center">
          <Globe2 size={28} className="mx-auto text-ess-gold-deep" strokeWidth={1.4} />
          <p className="ess-overline mt-6">Looking Ahead</p>
          <h2 className="font-display mt-4 text-4xl md:text-5xl text-ess-emerald leading-tight">
            Committed to measurable, meaningful impact.
          </h2>
          <span className="ess-divider mt-6" />
          <div className="mt-8 space-y-5 text-ess-charcoal/85 leading-relaxed">
            <p>
              As The Exit Strategy Society grows, we are committed to measuring
              and reporting meaningful outcomes related to education, professional
              development, digital literacy, entrepreneurship, and community impact.
            </p>
            <p>
              Our goal is to build programs and resources that create measurable
              improvements in the lives of the professionals we serve.
            </p>
          </div>
        </div>
      </section>

      <section className="ess-section bg-ess-emerald text-white">
        <div className="ess-container grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <h2 className="font-display text-4xl md:text-5xl leading-tight">
            We partner with organizations who share our standards.
          </h2>
          <div className="lg:text-right">
            <EmeraldButton
              as="link"
              to="/partnerships"
              data-testid="impact-cta-partner"
              className="bg-ess-gold text-ess-charcoal hover:bg-white"
            >
              Become a Partner <ArrowRight size={14} className="ml-3" />
            </EmeraldButton>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Impact;
